import { useMemo } from "react";
import { Menu, MenuItem } from "@progress/kendo-react-layout";

import { IconType } from "components/Icon/Icon";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { UserRole } from "models";

import { createLinkRender } from "./createLinkRender";
import { useResetMenuOnLeafSelect } from "./useResetMenuOnLeafSelect";

import "./ActionsMenuList.scss";

import type * as History from "history";
import type { UserState } from "models";

const HELP_PATH = process.env.REACT_APP_HELP_PATH ?? "";

interface MenuListType {
  alerts?: {
    onClick: () => void;
    count: number | undefined;
  };
  encounterOpened: boolean;
  hasChangesCallback?: (to: History.History.LocationDescriptor, clickHandler?: () => void) => void;
  isErrorMode: boolean;
  recoveredEncountersCount: number;
  showFullList: boolean;
  user: UserState;
}

export const ActionsMenuList = ({
  alerts,
  encounterOpened,
  hasChangesCallback,
  isErrorMode,
  recoveredEncountersCount,
  showFullList,
  user,
}: MenuListType) => {
  const recoveredEncounterToolTip =
    recoveredEncountersCount > 0 ?
      `You have ${recoveredEncountersCount} recovered encounters that require action.`
    : "";

  const { menuKey, onSelect, resetMenu } = useResetMenuOnLeafSelect();

  const linkRender = useMemo(
    () =>
      createLinkRender({
        hasChangesCallback,
        isErrorMode,
        encounterOpened,
        onNavigateAway: resetMenu,
      }),
    [hasChangesCallback, isErrorMode, encounterOpened, resetMenu],
  );

  const canOnlySystemSettings = user.isItAdmin && !user.isSystemAdmin;
  const { hasFAC, hasPSA, hasEither, hasSTU } = useFeatureFlags();

  const helpLink = hasSTU ? `${HELP_PATH.replace(/\/+$/, "")}/Education` : HELP_PATH;

  return (
    <Menu key={menuKey} linkRender={linkRender} onSelect={onSelect} openOnClick>
      {showFullList && (
        <MenuItem
          data={{
            icon: IconType.Settings,
            tooltip: "Settings",
          }}
        >
          {!canOnlySystemSettings && hasFAC && (
            <MenuItem
              text="Facilities"
              url="/settings/facility"
              disabled={!(user.isSystemAdmin || user.highestRole >= UserRole.ADMIN)}
              data={{
                icon: IconType.Facilities,
              }}
            />
          )}
          {!canOnlySystemSettings && hasPSA && (
            <MenuItem
              text="Practices"
              url="/settings/practice"
              disabled={!(user.isSystemAdmin || user.highestRole >= UserRole.ADMIN)}
              data={{
                icon: IconType.Practice,
              }}
            />
          )}
          <MenuItem
            text="System"
            url="/settings/system"
            disabled={!user.isItAdmin && !user.isSystemAdmin}
            data={{
              icon: IconType.Wrench,
            }}
          />
          <MenuItem
            text="Users"
            url="/settings/users"
            disabled={!user.isItAdmin && !user.isSystemAdmin}
            data={{
              icon: IconType.Users,
            }}
          />
          {!canOnlySystemSettings && hasEither && (
            <MenuItem
              text="Customize"
              url="/settings/customize/codesets"
              disabled={!user.privileges.canCustomizeSettings}
              data={{
                icon: IconType.Pencil,
              }}
            />
          )}
        </MenuItem>
      )}
      <MenuItem
        data={{
          icon: IconType.Help,
          tooltip: "Help",
        }}
      >
        <MenuItem
          text="Help"
          url={helpLink}
          data={{
            icon: IconType.HelpAlt,
            linkClass: "help",
            isExternal: true,
          }}
        />
        <MenuItem
          text="About"
          url="/about"
          data={{
            icon: IconType.InfoAlt,
          }}
        />
      </MenuItem>
      {showFullList &&
        hasEither &&
        user.highestRole !== UserRole.MANAGER &&
        !canOnlySystemSettings && (
          <MenuItem
            url="/recovery"
            data={{
              icon: IconType.History,
              linkClass: "recovery-link",
              tooltip: recoveredEncounterToolTip,
              badge: recoveredEncountersCount,
            }}
          />
        )}
      <MenuItem
        data={{
          icon: IconType.AlertNew,
          tooltip: "Alerts",
          badge: alerts?.count,
          onClick: alerts?.onClick,
        }}
      />
    </Menu>
  );
};
